<template>
  <div>
    <div
      v-for="item in items"
      :key="item.id"
      class="order-item-container"
      :style="
        !enableStore || disabledItems.includes(item.id)
          ? 'border-color: #EEEEEE; background-color: rgba(255, 255, 255, 0.5)'
          : ''
      "
    >
      <div>
        <p class="item-name" :style="!enableStore || disabledItems.includes(item.id) ? 'color: #9E9E9E' : ''">
          {{ item.name }}
        </p>
        <template v-if="getItemDescription(item)">
          <p
            :class="[
              'item-description',
              { collapsed: isDescriptionLong(item) && !expandedDescriptions.includes(item.id) },
            ]"
          >
            {{ getItemDescription(item) }}
          </p>
          <div
            v-if="isDescriptionLong(item)"
            class="expand-description-container"
            @click="toggleDescription(item)"
          >
            <span>
              {{ expandedDescriptions.includes(item.id) ? $t('readLess') : $t('readMore') }}
            </span>
            <v-icon color="#0054a5">
              {{ expandedDescriptions.includes(item.id) ? 'expand_less' : 'expand_more' }}
            </v-icon>
          </div>
        </template>
        <p v-if="isBelowMinimum(item)" class="item-min-warning">
          ⚠️ {{ $t('chooseMinimum') }} {{ item.itemMeta.min }}
          {{ item.itemMeta.min % 10 > 4 ? $t('tickets1') : $t('tickets2') }}
        </p>
      </div>
      <div class="right-container">
        <p
          class="price-amount-text"
          :style="!enableStore || disabledItems.includes(item.id) ? 'color: #9E9E9E' : ''"
        >
          {{ getDisplayPrice(item) }} {{ item.currency }}
        </p>
        <div class="quantity-buttons-container">
          <v-icon
            icon
            class="quantity-icon"
            :style="
              !getNumOfTicket(item) || !enableStore || disabledItems.includes(item.id)
                ? 'color: #9E9E9E !important'
                : 'color: #0054A5'
            "
            :disabled="!enableStore"
            @click="subOne(item)"
          >
            remove_circle
          </v-icon>
          <div
            class="quantity-text"
            :style="
              !enableStore || disabledItems.includes(item.id) ? 'color: #9E9E9E' : 'color: #0054A5'
            "
            translate="no"
          >
            {{ getNumOfTicket(item) / getItemStep(item) }}
          </div>
          <v-icon
            icon
            class="quantity-icon"
            :style="
              enableStore && !disabledItems.includes(item.id) && canAddOne(item)
                ? 'color: #0054a5'
                : 'color: #9E9E9E !important'
            "
            :disabled="!enableStore || disabledItems.includes(item.id) || !canAddOne(item)"
            @click="addOne(item)"
          >
            add_circle
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_ORGANIZATION_ORDER_ITEMS } from '@/graphql/event.graphql'
import { OrderItemType } from '@/types'

const MAX_ITEMS_PER_ORDER = 10

export default {
  name: 'sport-organizations-store-item-container',
  components: {},
  props: {
    organizationSlug: {
      type: String,
      default: null,
    },
    enableStore: {
      type: Boolean,
      default: false,
    },
    initialValues: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedItems: [...this.initialValues],
      items: null,
      loading: true,
      activeItemId: null,
      disabledItems: [],
      expandedDescriptions: [],
    }
  },
  mounted() {
    if (this.selectedItems.length) {
      this.activeItemId = this.selectedItems[0].id
    }
  },
  apollo: {
    getEventBySlug: {
      query: GET_ORGANIZATION_ORDER_ITEMS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          this.items = this.filterOrderItems(data.getOrganizationOrderItems)
        }
      },
    },
  },
  methods: {
    getNumOfTicket(item) {
      return this.selectedItems.filter(i => i.id === item.id).length
    },
    getItemDescription(item) {
      return (item.prepaidCouponInfo && item.prepaidCouponInfo.description) || item.description
    },
    isDescriptionLong(item) {
      const description = this.getItemDescription(item)

      return description.length > 200 || description.split('\n').length > 3
    },
    toggleDescription(item) {
      this.expandedDescriptions = this.expandedDescriptions.includes(item.id)
        ? this.expandedDescriptions.filter(id => id !== item.id)
        : [...this.expandedDescriptions, item.id]
    },
    getItemMin(item) {
      return (item.itemMeta && Number(item.itemMeta.min)) || 0
    },
    getItemMax(item) {
      return (item.itemMeta && Number(item.itemMeta.max)) || null
    },
    // A package is sold only as a whole bundle of `min` tickets (min === max > 1)
    isPackage(item) {
      const min = this.getItemMin(item)

      return min > 1 && min === this.getItemMax(item)
    },
    getItemStep(item) {
      return this.isPackage(item) ? this.getItemMin(item) : 1
    },
    getDisplayPrice(item) {
      return this.isPackage(item)
        ? Number((Number(item.price) * this.getItemStep(item)).toFixed(2))
        : item.price
    },
    isBelowMinimum(item) {
      const count = this.getNumOfTicket(item)

      return count > 0 && count < this.getItemMin(item)
    },
    hasItemsBelowMinimum() {
      return this.selectedItems.some(i => this.isBelowMinimum(i))
    },
    canAddOne(item) {
      const max = this.getItemMax(item)
      const step = this.getItemStep(item)

      return (
        this.selectedItems.length + step <= MAX_ITEMS_PER_ORDER &&
        (!max || this.getNumOfTicket(item) + step <= max)
      )
    },
    addOne(item) {
      if (!this.enableStore || this.disabledItems.includes(item.id) || !this.canAddOne(item)) {
        return
      }
      this.selectedItems.push(...Array(this.getItemStep(item)).fill(item))
      this.activeItemId = item.id
      this.emitOrder()
    },
    subOne(item) {
      if (this.selectedItems.some(i => i.id === item.id)) {
        let toRemove = this.getItemStep(item)
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id || toRemove-- <= 0)
        if (!this.getNumOfTicket(item)) {
          this.activeItemId = null
        }
        this.emitOrder()
      }
    },
    emitOrder() {
      this.$emit('updateOrder', this.selectedItems)
      this.$emit('updateValidity', !this.hasItemsBelowMinimum())
    },
    filterOrderItems(orderItems) {
      const orderItemType =
        this.$route.name === this.routes.shared.sportSubscriptionsStore
          ? OrderItemType.PREPAID_COUPON
          : OrderItemType.MEMBERSHIP_PLAN

      const showPackages = this.$route.query.packages === 'true'

      return orderItems.filter(
        orderItem => orderItem.type === orderItemType && this.isPackage(orderItem) === showPackages,
      )
    },
    disableSportMembershipTickets(tickets) {
      const sportMembershipTickets = tickets.filter(t => t.itemMeta.sportMembershipTicket)
      this.disabledItems = sportMembershipTickets.map(t => t.id)
      this.selectedItems = this.selectedItems.filter(i => !this.disabledItems.includes(i.id))
      const enabledItems = this.items.filter(item => !this.disabledItems.includes(item.id))
      const disabledItems = this.items.filter(item => this.disabledItems.includes(item.id))

      this.items = [...enabledItems, ...disabledItems]
      this.$emit('updateValidity', !this.hasItemsBelowMinimum())
    },
  },
}
</script>

<style scoped>
.order-item-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  gap: 2.5rem;
  border-radius: 0.25rem;
  border: 1px solid #0054a5;
  background: rgba(0, 130, 255, 0.15);
  margin-top: 1.5rem;
}
.right-container {
  display: flex;
  align-items: center;
  gap: 2.5rem;
  flex-shrink: 0;
}
.quantity-buttons-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}
@media screen and (max-width: 480px) {
  .right-container {
    flex-direction: column;
  }
  .quantity-buttons-container {
    flex-direction: row;
  }
}
.quantity-icon {
  user-select: none;
  cursor: pointer;
}
.item-name {
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  margin-bottom: 0;
}
.item-description {
  margin-top: 0.5rem;
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 125%;
  letter-spacing: -0.0375rem;
  margin-bottom: 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.item-description.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.expand-description-container {
  display: inline-flex;
  align-items: center;
  margin-top: 0.25rem;
  color: #0054a5;
  font-family: Raleway;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}
.item-min-warning {
  margin-top: 0.5rem;
  margin-bottom: 0;
  color: #333;
  font-family: Raleway;
  font-size: 0.875rem;
}
.price-amount-text {
  color: #333;
  text-align: right;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Source Sans Pro';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
  text-transform: uppercase;
  margin-bottom: 0;
}
.quantity-text {
  color: #0054a5;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Source Sans Pro';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
  text-transform: uppercase;
}
</style>
